import { useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahrutbp14a1h3mr6rfg";

type FormStatus = "idle" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const noticeItems = [
  "请尽量附上原始链接与截图，注明传播时间与来源账号，便于快速定位。",
  "涉及诈骗、冒名账号的线索，请保留完整聊天记录或页面截图。",
  "同一事项重复提交会合并处理，请勿重复发送相同内容。",
  "我们只对可核查的事实性问题进行说明，不对个人评价作判断。",
];

export default function ContactPage() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [messageLength, setMessageLength] = useState(0);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们",
        inLanguage: "zh-CN",
        description: "通过统一入口向黑料官方提交信息核实线索、冒名举报与联系需求。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "/contact" },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "联系我们 - 黑料官方线索提交与核实入口",
    description:
      "向黑料官方提交信息核实线索、冒名账号举报与合作咨询。请附上原始链接与截图并注明传播时间，我们会按顺序核查并在公告库同步结论。",
    keywords: "联系黑料官方,线索提交,冒名举报,信息核实入口,官方邮箱",
    path: "/contact",
    jsonLd,
  });

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageLength(event.target.value.length);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，我们已收到你的线索，会按顺序核查。");
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete("company_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setSubmitting(true);
    setStatus("idle");
    setFeedback("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功，我们已收到你的线索，会按顺序核查并通过邮件回复。");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFeedback(
          serverMsg && serverMsg !== responseText
            ? serverMsg
            : "提交未成功，请稍后重试，或直接发送邮件至 " + siteInfo.email,
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交未成功，请检查网络后重试，或直接发送邮件至 " + siteInfo.email);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          eyebrow="统一核实入口"
          title="联系我们"
          description="核实线索、冒名举报与合作咨询，统一从下方入口提交。提交后会生成受理编号，处理进度与结论会在声明公告库同步。"
          crumbs={[{ label: "首页", to: "/" }, { label: "联系我们" }]}
        />

        <section className="mx-auto grid max-w-content grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                    <i className="ri-mail-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500">澄清与核实</span>
                    <span className="break-all text-foreground-900">{siteInfo.email}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                    <i className="ri-alarm-warning-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500">冒名与诈骗举报</span>
                    <span className="break-all text-foreground-900">{siteInfo.reportEmail}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500">处理时间</span>
                    <span className="text-foreground-900">{siteInfo.serviceHours}</span>
                  </span>
                </li>
              </ul>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">提交须知</h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                {noticeItems.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center text-primary-600">
                      <i className="ri-check-double-line" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                <span className="flex h-5 w-5 items-center justify-center"><i className="ri-shield-check-line" /></span>
                安全提示
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-accent-900/85">
                官方不会索要短信验证码、登录密码或支付密码，也不会要求向个人账户转账。收到此类联系请先停止操作并在此提交核实。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">提交核实线索</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项。提交后我们会在 48 小时内开始处理。
              </p>

              <form className="mt-6 flex flex-col gap-5" onSubmit={handleSubmit} data-readdy-form noValidate>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                      称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="用于接收处理结果"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-type" className="text-sm font-medium text-foreground-800">
                      线索类型 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="contact-type"
                      name="type"
                      required
                      defaultValue=""
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                    >
                      <option value="" disabled>请选择线索类型</option>
                      <option value="疑似谣言核实">疑似谣言核实</option>
                      <option value="冒名账号举报">冒名账号举报</option>
                      <option value="诈骗信息反馈">诈骗信息反馈</option>
                      <option value="公告引用咨询">公告引用咨询</option>
                      <option value="合作与媒体沟通">合作与媒体沟通</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-link" className="text-sm font-medium text-foreground-800">
                      涉及链接
                    </label>
                    <input
                      id="contact-link"
                      name="link"
                      type="text"
                      maxLength={200}
                      placeholder="相关页面或内容的原始链接"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                      情况描述 <span className="text-primary-600">*</span>
                    </label>
                    <span className="text-xs text-foreground-400">{messageLength}/500</span>
                  </div>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    onChange={handleMessageChange}
                    placeholder="请描述你看到的说法、传播时间与来源，并说明你希望核实的具体问题。"
                    className="resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>

                <input
                  className="honeypot-field"
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status !== "idle" && (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`flex items-start gap-2.5 rounded-md border px-4 py-3 text-sm ${
                      status === "success"
                        ? "border-primary-300 bg-primary-50 text-primary-800"
                        : "border-primary-400 bg-primary-100 text-primary-900"
                    }`}
                  >
                    <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center">
                      <i className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                    </span>
                    <span>{feedback}</span>
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                    </span>
                    {submitting ? "提交中..." : "提交线索"}
                  </button>
                  <p className="text-xs leading-relaxed text-foreground-500">
                    提交即表示你已阅读并同意我们仅将上述信息用于本次核实与回复。
                  </p>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}